<script>
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import AddCannedModal from 'dashboard/routes/dashboard/settings/canned/AddCanned.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { conversationUrl, frontendURL } from '../../../helper/URLHelper';
import {
  ACCOUNT_EVENTS,
  CONVERSATION_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import MenuItem from '../../../components/widgets/conversation/contextMenu/menuItem.vue';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

// Keep in sync with backend EDIT_DELETE_TTL_SECONDS and the constant in
// components-next/message/Message.vue. UI uses the value to compute a
// human-readable countdown for the disabled-tooltip; the authoritative
// check still happens server-side.
const EDIT_DELETE_TTL_SECONDS = 3600;

export default {
  components: {
    AddCannedModal,
    MenuItem,
    ContextMenu,
    NextButton,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    enabledOptions: {
      type: Object,
      default: () => ({}),
    },
    contextMenuPosition: {
      type: Object,
      default: () => ({}),
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['open', 'close', 'replyTo'],
  setup() {
    const { getPlainText } = useMessageFormatter();

    return {
      getPlainText,
    };
  },
  data() {
    return {
      isCannedResponseModalOpen: false,
      showDeleteModal: false,
      showEditModal: false,
      editDraft: '',
      isSavingEdit: false,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
      getUISettings: 'getUISettings',
    }),
    plainTextContent() {
      return this.getPlainText(this.messageContent);
    },
    conversationId() {
      return this.message.conversation_id ?? this.message.conversationId;
    },
    messageId() {
      return this.message.id;
    },
    messageContent() {
      return this.message.content;
    },
    contentAttributes() {
      return useSnakeCase(
        this.message.content_attributes ?? this.message.contentAttributes
      );
    },
    // Resolves edit/delete gating. The parent (Message.vue) forwards the
    // computed flags so we don't need access to the auth store here.
    isOwnOutgoingMessage() {
      return !!this.enabledOptions.isOwnOutgoingMessage;
    },
    isEditWindowExpired() {
      return !!this.enabledOptions.isEditWindowExpired;
    },
    canInteractWithEdit() {
      return (
        this.enabledOptions.edit &&
        this.isOwnOutgoingMessage &&
        !this.isEditWindowExpired
      );
    },
    canInteractWithDelete() {
      return (
        this.enabledOptions.delete &&
        this.isOwnOutgoingMessage &&
        !this.isEditWindowExpired
      );
    },
    editTooltip() {
      if (!this.isOwnOutgoingMessage) {
        return this.$t('CONVERSATION.CONTEXT_MENU.EDIT_NOT_OWN_TOOLTIP');
      }
      if (this.isEditWindowExpired) {
        return this.$t('CONVERSATION.CONTEXT_MENU.EDIT_WINDOW_EXPIRED_TOOLTIP');
      }
      return '';
    },
    deleteTooltip() {
      if (!this.isOwnOutgoingMessage) {
        return this.$t('CONVERSATION.CONTEXT_MENU.EDIT_NOT_OWN_TOOLTIP');
      }
      if (this.isEditWindowExpired) {
        return this.$t('CONVERSATION.CONTEXT_MENU.EDIT_WINDOW_EXPIRED_TOOLTIP');
      }
      return '';
    },
    editDraftIsEmpty() {
      return (this.editDraft || '').trim().length === 0;
    },
    editDraftIsUnchanged() {
      return (
        (this.editDraft || '').trim() === (this.messageContent || '').trim()
      );
    },
    canSaveEdit() {
      return (
        !this.isSavingEdit &&
        !this.editDraftIsEmpty &&
        !this.editDraftIsUnchanged
      );
    },
  },
  methods: {
    async copyLinkToMessage() {
      const fullConversationURL =
        window.chatwootConfig.hostURL +
        frontendURL(
          conversationUrl({
            id: this.conversationId,
            accountId: this.currentAccountId,
          })
        );
      await copyTextToClipboard(
        `${fullConversationURL}?messageId=${this.messageId}`
      );
      useAlert(this.$t('CONVERSATION.CONTEXT_MENU.LINK_COPIED'));
      this.handleClose();
    },
    async handleCopy() {
      await copyTextToClipboard(this.plainTextContent);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      this.handleClose();
    },
    showCannedResponseModal() {
      useTrack(ACCOUNT_EVENTS.ADDED_TO_CANNED_RESPONSE);
      this.isCannedResponseModalOpen = true;
    },
    hideCannedResponseModal() {
      this.isCannedResponseModalOpen = false;
      this.handleClose();
    },
    handleOpen(e) {
      this.$emit('open', e);
    },
    handleClose(e) {
      this.$emit('close', e);
    },
    handleTranslate() {
      const { locale: accountLocale } = this.getAccount(this.currentAccountId);
      const agentLocale = this.getUISettings?.locale;
      const targetLanguage = agentLocale || accountLocale || 'en';
      this.$store.dispatch('translateMessage', {
        conversationId: this.conversationId,
        messageId: this.messageId,
        targetLanguage,
      });
      useTrack(CONVERSATION_EVENTS.TRANSLATE_A_MESSAGE);
      this.handleClose();
    },
    handleReplyTo() {
      this.$emit('replyTo', this.message);
      this.handleClose();
    },
    openDeleteModal() {
      // Hard-guard: even if the user somehow triggers the click on a
      // disabled item (keyboard etc.), refuse to open the destructive modal
      // when the TTL has expired or this isn't their message.
      if (!this.canInteractWithDelete) {
        if (this.deleteTooltip) {
          useAlert(this.deleteTooltip);
        }
        return;
      }
      this.handleClose();
      this.showDeleteModal = true;
    },
    async confirmDeletion() {
      try {
        await this.$store.dispatch('deleteMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
        });
        useAlert(this.$t('CONVERSATION.SUCCESS_DELETE_MESSAGE'));
        this.handleClose();
      } catch (error) {
        useAlert(this.$t('CONVERSATION.FAIL_DELETE_MESSSAGE'));
      }
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
    },
    openEditModal() {
      // Same hard-guard as delete: never open the editor for a message that
      // can't be edited.
      if (!this.canInteractWithEdit) {
        if (this.editTooltip) {
          useAlert(this.editTooltip);
        }
        return;
      }
      this.handleClose();
      this.editDraft = this.messageContent || '';
      this.showEditModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
      this.editDraft = '';
      this.isSavingEdit = false;
    },
    async confirmEdit() {
      if (!this.canSaveEdit) return;
      // Re-check the TTL right before submit. If the agent left the modal
      // open until the window closed, fail loudly instead of sending a doomed
      // request. The backend will reject it anyway, but this saves a round
      // trip and keeps the UX honest.
      const createdAt = Number(
        this.message.created_at ?? this.message.createdAt ?? 0
      );
      const nowSeconds = Math.floor(Date.now() / 1000);
      if (
        createdAt &&
        nowSeconds - createdAt > EDIT_DELETE_TTL_SECONDS
      ) {
        useAlert(
          this.$t('CONVERSATION.CONTEXT_MENU.EDIT_WINDOW_EXPIRED_TOOLTIP')
        );
        this.closeEditModal();
        return;
      }
      this.isSavingEdit = true;
      try {
        const accountId = this.currentAccountId;
        const newContent = this.editDraft.trim();
        // The backend exposes the agent edit endpoint at:
        //   PATCH /api/v1/accounts/:account_id/conversations/:conv_id/messages/:id
        // (added in this fork, not present in upstream Chatwoot). Body uses
        // a plain `content` key, same as the public Messages API.
        const response = await axios.patch(
          `/api/v1/accounts/${accountId}/conversations/${this.conversationId}/messages/${this.messageId}`,
          { content: newContent }
        );
        // Push the updated payload through the existing mutation so the
        // message list re-renders with the new content + edited flag.
        this.$store.dispatch('updateMessage', response.data);
        useAlert(this.$t('CONVERSATION.SUCCESS_EDIT_MESSAGE'));
        this.closeEditModal();
      } catch (error) {
        const status = error?.response?.status;
        // 403 from the backend means the TTL guard fired (or the agent isn't
        // the message author). Surface the same message the UI shows for the
        // disabled tooltip so the explanation is consistent.
        if (status === 403) {
          useAlert(
            this.$t('CONVERSATION.CONTEXT_MENU.EDIT_WINDOW_EXPIRED_TOOLTIP')
          );
        } else {
          useAlert(this.$t('CONVERSATION.FAIL_EDIT_MESSAGE'));
        }
        this.isSavingEdit = false;
      }
    },
    onEditKeydown(event) {
      // Cmd+Enter (mac) / Ctrl+Enter (win/linux): save
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        this.confirmEdit();
        return;
      }
      // Esc: cancel
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeEditModal();
      }
    },
  },
};
</script>

<template>
  <div class="context-menu">
    <!-- Add To Canned Responses -->
    <woot-modal
      v-if="isCannedResponseModalOpen && enabledOptions['cannedResponse']"
      v-model:show="isCannedResponseModalOpen"
      :on-close="hideCannedResponseModal"
    >
      <AddCannedModal
        :response-content="plainTextContent"
        :on-close="hideCannedResponseModal"
      />
    </woot-modal>
    <!-- Confirm Deletion -->
    <woot-delete-modal
      v-if="showDeleteModal && enabledOptions['delete']"
      v-model:show="showDeleteModal"
      class="context-menu--delete-modal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDeletion"
      :title="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE')"
      :message="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.MESSAGE')"
      :confirm-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.DELETE')"
      :reject-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')"
    />
    <!-- Edit message modal. Reuses woot-modal so visuals match the rest of
         the dashboard. The footer buttons mirror the delete modal pattern. -->
    <woot-modal
      v-if="showEditModal && enabledOptions['edit']"
      v-model:show="showEditModal"
      :on-close="closeEditModal"
      class="context-menu--edit-modal"
    >
      <div class="px-6 py-5">
        <h2 class="text-base font-medium text-n-slate-12 mb-4">
          {{ $t('CONVERSATION.CONTEXT_MENU.EDIT_MESSAGE_TITLE') }}
        </h2>
        <textarea
          v-model="editDraft"
          rows="5"
          class="w-full resize-y rounded-md border border-solid border-n-strong bg-n-background text-sm text-n-slate-12 p-3 outline-none focus:border-n-brand placeholder:text-n-slate-10"
          :placeholder="$t('CONVERSATION.CONTEXT_MENU.EDIT_PLACEHOLDER')"
          :disabled="isSavingEdit"
          @keydown="onEditKeydown"
        />
        <div class="flex items-center justify-end gap-2 mt-4">
          <NextButton
            ghost
            slate
            sm
            :label="$t('CONVERSATION.CONTEXT_MENU.CANCEL_EDIT')"
            :disabled="isSavingEdit"
            @click="closeEditModal"
          />
          <NextButton
            sm
            :label="$t('CONVERSATION.CONTEXT_MENU.SAVE_EDIT')"
            :disabled="!canSaveEdit"
            @click="confirmEdit"
          />
        </div>
      </div>
    </woot-modal>
    <NextButton
      v-if="!hideButton"
      ghost
      slate
      sm
      icon="i-lucide-ellipsis-vertical"
      class="invisible group-hover/context-menu:visible"
      @click="handleOpen"
    />
    <ContextMenu
      v-if="isOpen && !isCannedResponseModalOpen"
      :x="contextMenuPosition.x"
      :y="contextMenuPosition.y"
      @close="handleClose"
    >
      <div class="menu-container">
        <MenuItem
          v-if="enabledOptions['replyTo']"
          :option="{
            icon: 'arrow-reply',
            label: $t('CONVERSATION.CONTEXT_MENU.REPLY_TO'),
          }"
          variant="icon"
          @click.stop="handleReplyTo"
        />
        <MenuItem
          v-if="enabledOptions['copy']"
          :option="{
            icon: 'clipboard',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY'),
          }"
          variant="icon"
          @click.stop="handleCopy"
        />
        <MenuItem
          v-if="enabledOptions['translate']"
          :option="{
            icon: 'translate',
            label: $t('CONVERSATION.CONTEXT_MENU.TRANSLATE'),
          }"
          variant="icon"
          @click.stop="handleTranslate"
        />
        <hr />
        <MenuItem
          v-if="enabledOptions['copyLink']"
          :option="{
            icon: 'link',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY_PERMALINK'),
          }"
          variant="icon"
          @click.stop="copyLinkToMessage"
        />
        <MenuItem
          v-if="enabledOptions['cannedResponse']"
          :option="{
            icon: 'comment-add',
            label: $t('CONVERSATION.CONTEXT_MENU.CREATE_A_CANNED_RESPONSE'),
          }"
          variant="icon"
          @click.stop="showCannedResponseModal"
        />
        <hr v-if="enabledOptions['edit'] || enabledOptions['delete']" />
        <!-- Edit own outgoing message. Always rendered when the message type
             qualifies (outgoing + has text), but disabled with a tooltip
             when TTL expired or sender is not the current agent. -->
        <div
          v-if="enabledOptions['edit']"
          v-tooltip.right="editTooltip"
          class="menu-item-wrap"
          :class="{ 'menu-item-wrap--disabled': !canInteractWithEdit }"
        >
          <MenuItem
            :option="{
              icon: 'edit',
              label: $t('CONVERSATION.CONTEXT_MENU.EDIT_MESSAGE'),
            }"
            variant="icon"
            @click.stop="openEditModal"
          />
        </div>
        <!-- Delete: same wrapper pattern. Upstream behaviour preserved when
             enabled; TTL/ownership gates added on top. -->
        <div
          v-if="enabledOptions['delete']"
          v-tooltip.right="deleteTooltip"
          class="menu-item-wrap"
          :class="{ 'menu-item-wrap--disabled': !canInteractWithDelete }"
        >
          <MenuItem
            :option="{
              icon: 'delete',
              label: $t('CONVERSATION.CONTEXT_MENU.DELETE'),
            }"
            variant="icon"
            @click.stop="openDeleteModal"
          />
        </div>
      </div>
    </ContextMenu>
  </div>
</template>

<style lang="scss" scoped>
.menu-container {
  @apply p-1 bg-n-background shadow-xl rounded-md;

  hr:first-child {
    @apply hidden;
  }

  hr {
    @apply m-1 border-b border-solid border-n-strong;
  }
}

// Wrapper around MenuItem to host the v-tooltip directive AND a disabled
// state (greyed out + click-through swallowed via pointer-events:none on the
// inner button-like row). Keeps the underlying menuItem.vue component
// untouched so unrelated places that use it aren't affected.
.menu-item-wrap {
  @apply block;

  &--disabled {
    @apply opacity-50 cursor-not-allowed;

    > .menu {
      @apply pointer-events-none;
    }
  }
}

.context-menu--delete-modal {
  ::v-deep {
    .modal-container {
      @apply max-w-[30rem];

      h2 {
        @apply font-medium text-base;
      }
    }
  }
}

.context-menu--edit-modal {
  ::v-deep {
    .modal-container {
      @apply max-w-[34rem];
    }
  }
}
</style>
